import React, { useState, useEffect } from 'react';
import { Paper, Title, Text, Stack, LoadingOverlay, Button, Alert } from '@mantine/core';
import { useSalon } from '../../context/SalonContext';
import { useApp } from '../../context/AppContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { IconCheck, IconAlertCircle } from '@tabler/icons-react';

function PaymentSettings() {
  const { businessInfo, setBusinessInfo, currentUser, setCurrentUser, employees, setEmployees } = useSalon();
  const { createStripeConnectAccount, getStripeConnectAccountLink, updateSalon, updateStaff } = useApp();
  const [stripeConnectAccountId, setStripeConnectAccountId] = useState(businessInfo?.stripe_connect_account_id);
  const [isLoading, setIsLoading] = useState(true);
  const [countdown, setCountdown] = useState(10);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (businessInfo) {
      setIsLoading(false);
      setStripeConnectAccountId(businessInfo.stripe_connect_account_id);
    }
  }, [businessInfo]);

  // Handle success redirect countdown
  useEffect(() => {
    if (searchParams.get('success') === 'true') {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            navigate('/admin/payments');
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [searchParams, navigate]);

  const handleCreateStripeConnectAccount = async () => {
    try {
        setIsLoading(true);
        const responseJson = await createStripeConnectAccount(businessInfo, currentUser);
        console.log("responseJson", responseJson);
        if (responseJson?.accountId) {
            syncStripeConnectAccountToSalon(responseJson.accountId);
            syncStripeConnectAccountToCurrentUser(responseJson.accountId);
            setStripeConnectAccountId(responseJson.accountId);
        }
        setIsLoading(false);
    } catch (error) {
        console.error('Error creating stripe connect account:', error);
        setIsLoading(false);
    }
  }

  const syncStripeConnectAccountToSalon = async (stripeAccountId) => {
    const businessInfoWithStripeAccountId = {
        ...businessInfo,
        stripe_connect_account_id: stripeAccountId
    }
    await updateSalon(businessInfoWithStripeAccountId);
    setBusinessInfo(businessInfoWithStripeAccountId);
  }

  const syncStripeConnectAccountToCurrentUser = async (stripeAccountId) => {
    const currentUserWithStripeAccountId = {
        ...currentUser,
        stripe_connect_account_id: stripeAccountId
    }
    await updateStaff(currentUserWithStripeAccountId);
    setCurrentUser(currentUserWithStripeAccountId);
    setEmployees(employees.map(emp => ({
        ...emp,
        stripe_connect_account_id: emp.id === currentUser.id ? stripeAccountId : emp.stripe_connect_account_id
    })));
  }

  const handleGetStripeConnectAccountLink = async () => {
    try {
        setIsLoading(true);
        const responseJson = await getStripeConnectAccountLink(stripeConnectAccountId);
        console.log("responseJson", responseJson);

        if (responseJson?.url) {
            setIsLoading(false);
            window.open(responseJson?.url, '_blank');
        }
    } catch (error) {
        console.error('Error getting stripe connect account link:', error);
        setIsLoading(false);
    }
  }

  const renderStatusAlert = () => {
    if (searchParams.get('success') === 'true') {
      return (
        <Alert 
          icon={<IconCheck size="1.1rem" />} 
          title="Success!" 
          color="green" 
          mb="md"
        >
          Successfully onboarded Stripe Connect, redirecting to Payments Dashboard in {countdown} seconds
        </Alert>
      );
    }

    if (searchParams.get('canceled') === 'true') {
      return (
        <Alert 
          icon={<IconAlertCircle size="1.1rem" />} 
          title="Warning" 
          color="yellow" 
          mb="md"
        >
          Warning: Stripe Account Onboarding is incomplete. Access to Payments will be restricted until onboarding is completed.
        </Alert>
      );
    }

    return null;
  };

  const renderPaymentSettings = () => {
    if (businessInfo?.status === "Trial" || businessInfo?.status === "Inactive") {
      return (
        <Stack>
          <Text c="dimmed" size="sm">
            You must have an active subscription in order to access Payments. Please upgrade to a paid plan before continuing.
          </Text>
        </Stack>
      )
    }

    if (businessInfo?.stripe_connect_account_is_onboarded) {
      return (
        <Stack>
          <Text c="dimmed" size="sm">
            You have successfully created and onboarded your Stripe Account.  Visit the Payments Dashboard to manage your payments.
          </Text>
          <Button
            onClick={() => {
              navigate('/admin/payments');
            }}
            color="violet"
          >
            Go to Payments Dashboard
          </Button>
        </Stack>
      )
    }

    if (stripeConnectAccountId) {
      const message = businessInfo?.stripe_connect_account_id 
        ? "Finish setting up your Stripe Account to start accepting in-store payments." 
        : "Begin setting up your Stripe Account to start accepting in-store payments.";
      return (
        <Stack>
            <Text c="dimmed" size="sm">{message}</Text>
            <Text c="dimmed" size="sm">Note: A website or social media URL associated with your business is required to onboard your Stripe Account.</Text>
            <Button
                onClick={handleGetStripeConnectAccountLink}
                color="violet"
            >
              Setup your Stripe Account
            </Button>
        </Stack>
      )
    }

    return (
      <>
        <Title order={3}>Interested in accepting in-store payments?</Title>
          <Button
              onClick={handleCreateStripeConnectAccount}
              color="violet"
          >
              Create an account!
          </Button>
      </>
    )
  }

  return (
    <Stack>
      {isLoading && <LoadingOverlay visible={isLoading} />}
      {!isLoading && (
        <Stack p="md">
          {renderStatusAlert()}
          <Title order={3}>Payment Settings</Title>
          {renderPaymentSettings()}
        </Stack>
      )}
    </Stack>
  );
}

export default PaymentSettings; 